import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}
/* eslint-disable @typescript-eslint/no-unused-vars */

/* eslint-disable default-case */

/* eslint-disable no-param-reassign */


import React, { useRef, useCallback, useState } from 'react';
import styled from 'styled-components';
import { Resizer } from "./Resizer";
import { motion, useMotionValue } from 'framer-motion';
import { theme } from "../../theme";
import { hidePanel, showPanel, resizePanel } from "./animation-strategies";
import { useFlip } from "../../hooks/useFlip";
import { PanelTransitionStatusProvider } from "./usePanelTransitionStatus";
import { getBackgroundColor } from "../../theme/color-tokens";
export function Panel(_ref) {
  var children = _ref.children,
      _ref$background = _ref.background,
      background = _ref$background === void 0 ? 'panel' : _ref$background,
      resizer = _ref.resizer,
      area = _ref.area,
      onPanStart = _ref.onPanStart,
      onPan = _ref.onPan,
      onPanEnd = _ref.onPanEnd,
      _ref$gap = _ref.gap,
      gap = _ref$gap === void 0 ? 1 : _ref$gap,
      _ref$borderRadius = _ref.borderRadius,
      borderRadius = _ref$borderRadius === void 0 ? 8 : _ref$borderRadius,
      _ref$zIndex = _ref.zIndex,
      zIndex = _ref$zIndex === void 0 ? theme.zIndices.panel : _ref$zIndex,
      className = _ref.className,
      isShowing = _ref.isShowing,
      _ref$keepMounted = _ref.keepMounted,
      keepMounted = _ref$keepMounted === void 0 ? false : _ref$keepMounted,
      pointerEvents = _ref.pointerEvents;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      panning = _useState2[0],
      setPanning = _useState2[1];

  var _useState3 = useState(isShowing),
      _useState4 = _slicedToArray(_useState3, 2),
      mountTransitionComplete = _useState4[0],
      setMountTransitionComplete = _useState4[1]; // If the panel is showing when Panels is first mounted, set to true.


  var targetWidth = useMotionValue('100%');
  var targetHeight = useMotionValue('100%'); // Should the children actually be rendered. This defers unmounting of
  // children until the exit animation is complete.

  var _useState5 = useState(isShowing),
      _useState6 = _slicedToArray(_useState5, 2),
      shouldMount = _useState6[0],
      setShouldMount = _useState6[1];

  var ref = useRef(null);
  var activeTransition = useRef(null);
  /**
   * Pan start
   */

  var handlePanStart = useCallback(function () {
    onPanStart(area, resizer);
    setPanning(true);
  }, [onPanStart, area, resizer]);
  /**
   * Pan
   */

  var handlePan = useCallback(function (_, info) {
    onPan(isColumnDimension(resizer) ? info.offset.x : info.offset.y);
  }, [onPan, resizer]);
  /**
   * Mouse down handler.
   * This prevents the cursor from changing in safari while
   * resizing a panel.
   */

  var onMouseDown = useCallback(function (e) {
    e.preventDefault();
  }, []);
  /**
   * Animate after FLIP measurements
   */

  var animate = useCallback(function (targetRef, keyframes, info, _ref2) {
    var previousTemplate = _ref2.previousTemplate,
        template = _ref2.template,
        _ref2$transitionType = _ref2.transitionType,
        transitionType = _ref2$transitionType === void 0 ? 'toggle' : _ref2$transitionType; // We need to treat mounting, unmounting, and resizing panels differently.
    // Those which are being toggled need to be positioned outside of
    // the grid, using fixed positioning. Once the animation is finished,
    // we immediately position the element within the grid.

    var wasVisible = previousTemplate.visible.includes(area);
    var isVisible = template.visible.includes(area);
    var el = targetRef.current;

    var options = _objectSpread(_objectSpread({}, info), {}, {
      gridArea: area,
      activeTransition: activeTransition,
      keyframes: keyframes
    }); // Hiding


    if (wasVisible && !isVisible) {
      hidePanel(el, transitionType, options, function () {
        if (!keepMounted) {
          setShouldMount(false);
        }

        setMountTransitionComplete(false);
      });
      return;
    } // Showing


    if (!wasVisible && isVisible) {
      setShouldMount(true);
      showPanel(el, transitionType, options, function () {
        setMountTransitionComplete(true);
      });
      return;
    } // Prevent animating if the panel hasn't either changed
    // size or position.


    if (options.deltaX === 0 && options.deltaY === 0 && options.deltaH === 1 && options.deltaW === 1) {
      return;
    } // Persisting and resizing or repositioning


    targetWidth.set("".concat(options.after.width, "px"));
    targetHeight.set("".concat(options.after.height, "px"));
    resizePanel(el, transitionType, options, function () {
      targetWidth.set('100%');
      targetHeight.set('100%');
    });
  }, [area, keepMounted, targetHeight, targetWidth]);
  var handlePanEnd = useCallback(function () {
    setPanning(false);
    onPanEnd();
  }, [onPanEnd]);
  /**
   * Reset any transition styles before gathering FLIP
   * layout measurements.
   */

  var resetActiveAnimations = useCallback(function (el) {
    // cancel any active animations
    if (activeTransition.current) {
      activeTransition.current.cancel();
    } // remove any existing inline styles, and set the grid-area


    el.style.cssText = "grid-area: ".concat(area);
  }, [area]);
  useFlip(ref, {
    animate: animate,
    reset: resetActiveAnimations,
    generateKeyframes: false
  });
  return /*#__PURE__*/React.createElement(React.Fragment, {
    key: area
  }, /*#__PURE__*/React.createElement(PanelTransitionStatusProvider, {
    mountTransitionComplete: mountTransitionComplete
  }, /*#__PURE__*/React.createElement(StyledPanel, {
    $showing: isShowing,
    $gap: gap,
    $zIndex: zIndex,
    $borderRadius: borderRadius,
    $background: background,
    ref: ref,
    style: {
      gridArea: area,
      pointerEvents: pointerEvents
    },
    className: className
  }, shouldMount && children)), resizer && isShowing && /*#__PURE__*/React.createElement(Resizer, {
    style: {
      gridArea: area
    },
    placement: resizer,
    onPanStart: handlePanStart,
    onPan: handlePan,
    onPanEnd: handlePanEnd,
    onMouseDown: onMouseDown,
    isPanning: panning
  }));
}
var StyledPanel = styled(motion.div).withConfig({
  displayName: "Panel__StyledPanel",
  componentId: "vapor__sc-1abgzt4-0"
})(["transform-origin:top left;top:", ";left:", ";right:", ";bottom:", ";position:absolute;contain:size paint;overflow:hidden;z-index:", ";background:", ";border-radius:", ";pointer-events:none;", ""], function (p) {
  return "".concat(p.$gap, "px");
}, function (p) {
  return "".concat(p.$gap, "px");
}, function (p) {
  return "".concat(p.$gap, "px");
}, function (p) {
  return "".concat(p.$gap, "px");
}, function (p) {
  return p.$zIndex;
}, function (p) {
  return p.$background === 'transparent' ? 'transparent' : getBackgroundColor(p.$background);
}, function (p) {
  return typeof p.$borderRadius === 'string' ? p.$borderRadius : theme.radii[p.$borderRadius];
}, function (p) {
  return p.$showing && "\n    pointer-events: auto;\n  ";
});
export function isColumnDimension(resizer) {
  return resizer === 'left' || resizer === 'right';
}